/**
 * TaxableValueInputs - Core Component (Production-Ready)
 * Clean input component for tax value configuration.
 * Used in: Project workflow pages
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DOMAIN KNOWLEDGE: Taxable Value Projections (Michigan Municipal Bonds)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This component configures the revenue base for millage calculations.
 * Taxable value projections determine how much revenue capacity exists
 * for bond debt service payments.
 *
 * Key Concepts:
 *
 * 1. INITIAL TAXABLE VALUE
 *    - Starting property value for the bond project
 *    - Must be greater than 0
 *    - Acts as the baseline for all future projections
 *    - Often a placeholder value; actual value determined by assessment
 *
 * 2. GROWTH RATE TIERS
 *    - Three-tier system for conservative projections:
 *      a) Starting Year (Year 0): Initial growth rate
 *      b) Five Year Rate (Years 1-5): Near-term growth assumption
 *      c) Twenty Year Rate (Years 6+): Long-term growth assumption
 *    - Growth rates are percentages (0-100% in form, 0-1 decimal in engine)
 *    - Conservative projections ensure bonds remain viable if property
 *      values grow slower than expected
 *
 * 3. MUNICIPAL FINANCE CONTEXT
 *    - Advisors use historical data for regulatory-compliant growth rates
 *    - 5-year averages used for near-term projections
 *    - 20-year averages used for long-term projections
 *    - Often kept steady to avoid over-anticipating revenue capacity
 *    - Conservative approach protects bondholders and taxpayers
 *
 * 4. PROJECTION TIMELINE
 *    - Projections dynamically calculated based on bond series timeline
 *    - Must cover all bond payment dates plus safety buffer
 *    - Default minimum: 50 years (standard project duration)
 *    - Extended automatically if bonds extend beyond default timeline
 *
 * Why This Matters:
 * - Revenue capacity DRIVES bond sizing (not the other way around)
 * - Conservative projections protect against revenue shortfalls
 * - Growth rate assumptions directly impact millage calculations
 * - Over-optimistic projections can lead to bond defaults
 *
 * Processing Pipeline Impact:
 * - Taxable value map feeds into millage calculations
 * - Millage determines revenue capacity at each payment date
 * - Revenue capacity determines maximum bond principal
 * - Backwards distribution uses revenue capacity to size bonds
 *
 * Common Mistake:
 * ❌ Don't use overly optimistic growth rates
 * ❌ Don't assume linear growth (use tiered rates)
 * ✅ Use historically-based, conservative growth rates
 * ✅ Ensure projections cover full bond timeline
 *
 * For More Context:
 * @see apps/web/src/builders/taxable-value/_taxable-value-info.md - Builder overview
 * @see docs/business-context/process-overview.md - How taxable value feeds into processing pipeline
 */

import { Box, Heading, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import type React from 'react';
import { CurrencyInput, PercentInput } from '~/components/inputs';
import { useSidebar } from '~/components/layout/shared/SidebarContext';
import type { TaxValueInputs, TaxValueInputsForm } from '~/types';
import { useTaxableValueInputs } from './useTaxableValueInputs';

interface TaxableValueInputsProps {
  values: TaxValueInputsForm;
  onUpdate: (values: TaxValueInputsForm) => void;
  onBlur?: () => void;
  showValidation?: boolean;
  isDisabled?: boolean;
}

export const TaxableValueInputs: React.FC<TaxableValueInputsProps> = ({
  values,
  onUpdate,
  onBlur,
  showValidation = false,
  isDisabled = false,
}) => {
  const { isExpanded } = useSidebar();

  // Extract all business logic from hook
  const { handlers, computed, refs, constants } = useTaxableValueInputs({
    values,
    onUpdate,
    onBlur,
    showValidation,
  });

  // Destructure for cleaner code
  const { handleInputChange, markTouched } = handlers;
  const { validationErrors, allFieldsTouched } = computed;
  const { initialTaxableValueInputRef } = refs;
  const { MIN_PERCENT, MAX_PERCENT, MIN_CURRENCY } = constants;

  return (
    <VStack
      align="stretch"
      boxShadow="md"
      gap={6}
      border="1px solid"
      borderColor="gray.200"
      p={4}
      borderRadius="md"
      bg={isDisabled ? 'gray.50' : undefined}
      pointerEvents={isDisabled ? 'none' : undefined}
      opacity={isDisabled ? 0.8 : 1}
    >
      <Heading size="md">Taxable Value Projections</Heading>

      <SimpleGrid columns={{ base: 4, md: isExpanded ? 2 : 4 }} gap={3} alignItems="flex-end">
        <CurrencyInput
          ref={initialTaxableValueInputRef}
          label={isExpanded ? 'Initial Value' : 'Initial Taxable Value'}
          size="sm"
          value={values.initialTaxableValue || ''}
          onChange={(value) => handleInputChange('initialTaxableValue', value)}
          onBlur={() => markTouched('initialTaxableValue')}
          min={MIN_CURRENCY}
          isRequired
        />

        <PercentInput
          label={isExpanded ? 'Start Year Rate' : 'Start Year Growth Rate'}
          size="sm"
          value={values.startYearGrowthRate || ''}
          onChange={(value) => handleInputChange('startYearGrowthRate', value)}
          onBlur={() => markTouched('startYearGrowthRate')}
          min={MIN_PERCENT}
          max={MAX_PERCENT}
          isRequired
        />

        <PercentInput
          label={isExpanded ? 'Five Year Rate' : 'Five Year Growth Rate'}
          size="sm"
          value={values.fiveYearGrowthRate || ''}
          onChange={(value) => handleInputChange('fiveYearGrowthRate', value)}
          onBlur={() => markTouched('fiveYearGrowthRate')}
          min={MIN_PERCENT}
          max={MAX_PERCENT}
          isRequired
        />

        <PercentInput
          label={isExpanded ? 'Twenty Year Rate' : 'Twenty Year Growth Rate'}
          size="sm"
          value={values.twentyYearGrowthRate || ''}
          onChange={(value) => handleInputChange('twentyYearGrowthRate', value)}
          onBlur={() => markTouched('twentyYearGrowthRate')}
          min={MIN_PERCENT}
          max={MAX_PERCENT}
          isRequired
        />
      </SimpleGrid>

      {/* Validation Messages */}
      {(showValidation || allFieldsTouched) && validationErrors.length > 0 && (
        <Box
          mt={4}
          p={3}
          bg="red.50"
          borderRadius="md"
          borderLeft="4px solid"
          borderColor="red.500"
        >
          <Text fontSize="xs" fontWeight="bold" color="red.600" mb={2}>
            Please fix the following issues:
          </Text>
          <VStack align="flex-start" gap={1}>
            {validationErrors.map((error) => (
              <Text key={error} fontSize="xs" color="red.600">
                • {error}
              </Text>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  );
};

// Utility function to convert form values to engine format
export const convertToEngineFormat = (formValues: TaxValueInputsForm): TaxValueInputs => {
  // Helper to parse formatted currency values
  const parseFormattedNumber = (value: string): number => {
    if (!value || value.trim() === '') return 0;
    const cleaned = value.replace(/[^\d.-]/g, '');
    return parseFloat(cleaned) || 0;
  };

  return {
    startingYearTaxableValue: parseFormattedNumber(formValues.initialTaxableValue),
    startingYearGrowthRate: parseFloat(formValues.startYearGrowthRate) / 100 || 0,
    fiveYearRate: parseFloat(formValues.fiveYearGrowthRate) / 100 || 0,
    twentyYearRate: parseFloat(formValues.twentyYearGrowthRate) / 100 || 0,
  };
};

// Utility function to convert engine format to form values
export const convertFromEngineFormat = (engineValues: TaxValueInputs): TaxValueInputsForm => {
  return {
    initialTaxableValue: engineValues.startingYearTaxableValue.toString(),
    startYearGrowthRate: (engineValues.startingYearGrowthRate * 100).toString(),
    fiveYearGrowthRate: (engineValues.fiveYearRate * 100).toString(),
    twentyYearGrowthRate: (engineValues.twentyYearRate * 100).toString(),
  };
};

export type { TaxValueInputsForm, TaxValueInputs };
